












































































import type { PropType } from '@nuxtjs/composition-api';
// eslint-disable-next-line no-duplicate-imports
import { computed, defineComponent } from '@nuxtjs/composition-api';

import { useDeclination } from '@/diptyqueTheme/composable/useDeclination';
import type { MediaPreloadableSection } from '~/diptyqueTheme/types/components';
// eslint-disable-next-line no-duplicate-imports
import type {
  HeroHeaderModel,
  VisualPositionOption
} from '~/diptyqueTheme/types/contentful';
import { VisualPosition } from '~/diptyqueTheme/types/contentful';
import { isValidRatio } from '~/helpers/ratioValueValidatior';

export default defineComponent({
  name: 'HeroHeader',
  components: {
    VaimoBanner: () => import('atoms/VaimoBanner.vue'),
    VaimoButton: () => import('atoms/VaimoButton.vue'),
    VaimoHeading: () => import('atoms/VaimoHeading.vue'),
    VaimoTruncatedText: () => import('atoms/VaimoTruncatedText.vue')
  },
  props: {
    sectionData: {
      required: true,
      type: Object as PropType<HeroHeaderModel & MediaPreloadableSection>
    },
    pageName: {
      type: String,
      required: false,
      default: ''
    },
    pageType: {
      type: String,
      required: false,
      default: ''
    }
  },
  setup(props) {
    const {
      getBannerData: bannerData,
      getButtonData: buttonData,
      getButtonDataFunction
    } = useDeclination(props.sectionData);

    /**
     * Default aspect ratios for mobile and desktop.
     * - Mobile legacy default is '5:6' ("Choose a Value")
     * - Mobile default is '9:16' ("Default for Current Module")
     * - Desktop default is '16:9' ("Default for Current Module")
     */
    const DEFAULT_RATIO = {
      MOBILE: '9:16',
      MOBILE_LEGACY: '5:6',
      DESKTOP: '16:9'
    };

    /**
     * Computes the aspect ratio for mobile and desktop based on the section data.
     * If the ratio is invalid, it defaults to predefined values.
     */
    const aspectRatio = computed<{
      mobile: string;
      desktop: string;
    }>(() => {
      const { ratio, ratioD } = props.sectionData;

      // If ratio is not defined, use the legacy mobile ratio
      // kept for compatibility with older data
      if (typeof ratio === 'undefined') {
        return {
          mobile: DEFAULT_RATIO.MOBILE_LEGACY,
          desktop: isValidRatio(ratioD) ? ratioD : DEFAULT_RATIO.DESKTOP
        };
      }

      return {
        mobile: isValidRatio(ratio) ? ratio : DEFAULT_RATIO.MOBILE,
        desktop: isValidRatio(ratioD) ? ratioD : DEFAULT_RATIO.DESKTOP
      };
    });

    /**
     * Checks if the banner is visible.
     * This is determined by the `visible` property of the banner data.
     * @returns {boolean} - Returns true if the banner is visible, false otherwise
     */
    const hasVisual = computed<boolean>(
      () => bannerData.value?.visible || false
    );

    /**
     * Computes the effective visual position based on the section data.
     * Note: When contentPosition (Contentful: Heading + Description + CTA Location field) is set, the position is inverted:
     * - TOP becomes BOTTOM (content on top → visual on bottom)
     * - BOTTOM becomes TOP (content on bottom → visual on top)
     * Otherwise, uses visualPosD (Visual Position on Desktop) or defaults to LEFT.
     * @returns {VisualPositionOption} - Returns the computed visual position
     */
    const effectiveVisualPosition = computed<VisualPositionOption>(() => {
      if (props.sectionData.contentPosition === VisualPosition.TOP) {
        return VisualPosition.BOTTOM;
      }
      if (props.sectionData.contentPosition === VisualPosition.BOTTOM) {
        return VisualPosition.TOP;
      }
      return props.sectionData.visualPosD || VisualPosition.LEFT;
    });

    /**
     * Computes the buttons data for primary, secondary, and visual buttons.
     * where primary is the main button, secondary is an optional button,
     * and visual is a button that appears on the visual banner.
     * @returns {Object} - An object containing the button data for primary, secondary, and visual buttons
     */
    const buttonsData = computed(() => {
      const { visualButton, secondaryButton } = props.sectionData;

      return {
        primary: buttonData.value,
        secondary: getButtonDataFunction(secondaryButton),
        visual: getButtonDataFunction(visualButton)
      };
    });

    /**
     * Defines whether the header should be treated as the main heading (h1) or a secondary heading (h2).
     * Defaults to true (h1) if the property is not defined.
     * @returns {boolean} - Returns true if the header is level one (h1), false if level two (h2)
     */
    const isHeaderLevelOne = computed<boolean>(() => {
      return props.sectionData?.headingLevelOne ?? true;
    });

    /**
     * Computes if the visual should fit within the container on desktop.
     * @returns {boolean} - Returns true when `isVisualFullscreenOnDesktop` is false and the visual position is either TOP or BOTTOM.
     */
    const fitDesktopVisInContainer = computed(() => {
      // If fullscreen is disabled, check visual position
      if (props.sectionData?.isFullscreenOnDesktop === false) {
        return (
          effectiveVisualPosition.value === VisualPosition.TOP ||
          effectiveVisualPosition.value === VisualPosition.BOTTOM
        );
      }
      // Default: don't fit in container (use fullscreen)
      return false;
    });

    return {
      aspectRatio,
      effectiveVisualPosition,
      hasVisual,
      bannerData,
      buttonsData,
      isHeaderLevelOne,
      fitDesktopVisInContainer
    };
  }
});
